import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { HttpModule } from '@angular/http';
import { CommonModule, registerLocaleData } from '@angular/common';
import br from '@angular/common/locales/pt';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EFFECTS } from './store/effects';
import { metaReducers, REDUCERS } from './store/reducers';

import { AppRoutingModule } from './app-routing.module';
import { ComponentsModule } from './components/components.module';
import { ModalModule } from './components/modal/modal.module';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CompanyModule } from './pages/company/company.module';
import { EstablishmentModule } from './pages/establishment/establishment.module';
import { ProfileModule } from './pages/profile/profile.module';
import { ShareButtonsModule } from 'ngx-sharebuttons';
import { OwnPartnershipModule } from './pages/own-partnership/own-partnership.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CampaignModule } from './pages/campaign/campaign.module';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { MemberGetMemberModule } from 'src/app/pages/member-get-member/member-get-member.module';

import { HomeComponent } from './pages/home';
import { cuponsComponent } from './pages/cupons';
import { LoginComponent } from './pages/login';
import { LogoutComponent } from './pages/logout';
import { AboutComponent } from './pages/about';
import { ListingPageComponent } from './pages/listing/listing-page.component';
import { NoContentComponent } from './pages/no-content';
import { ToasterComponent } from './directives/toaster/toaster.component';
import { SimulatorPageComponent } from './pages/simulator';
import { RedirectSSOComponent } from './pages/redirect-sso';
import { StatusComponent } from './pages/status/status.component';

import { CookieService } from 'ngx-cookie-service';
import { ToasterService } from './services/toaster.service';
import { TrialToasterService } from './services/trial-toaster.service';
import { SearchService } from './services/search.service';
import { UserService } from './services/user.service';
import { StorageService } from './services/storage.service';
import { ReportsService } from './services/report.service';
import { RequestsServices } from './services/requests.service';
import { EstablishmentsService } from './services/establishments.service';
import { CashbackService } from './services/cashback.service';
import { WalletService } from './services/wallet.service';
import { OfferService } from './services/offer.service';
import { AccountsService } from './services/accounts.service';
import { GTMService } from './services/gtm.service';
import { VouchersService } from './services/vouchers.service';
import { InvitesService } from './services/invites.service';
import { EstablishmentRedirectionService } from './services/establishment-redirection.service';

import { AppComponent } from './app.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthGuard, FremiumGuard } from './guards';
import { environment } from '../environments/environment';
import { ReportsModule } from './pages/reports/reports.module';
import { DragDropDirective } from './directives/drag-drop.directive';
import { BsModalService, ModalModule as NgxModalModule } from 'ngx-bootstrap';
import { NgTicketsModule } from './components/tickets/ng-tickets.module';
import { EmptyPage } from './pages/empty/empty.page';

export function HttpLoaderFactory(http: HttpClient) {
  // Os arquivos de i18n não têm hash no nome: o parâmetro evita que o navegador use uma versão antiga em cache.
  return new TranslateHttpLoader(http, 'assets/i18n/', `.json?v=${Date.now()}`);
}

registerLocaleData(br, 'pt-BR');

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    HomeComponent,
    cuponsComponent,
    LoginComponent,
    LogoutComponent,
    AboutComponent,
    ListingPageComponent,
    NoContentComponent,
    ToasterComponent,
    RedirectSSOComponent,
    EmptyPage,
    SimulatorPageComponent,
    DragDropDirective,
    StatusComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    ComponentsModule,
    ModalModule,
    NgxDatatableModule,
    HttpModule,
    BrowserAnimationsModule,
    ComponentsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    ReportsModule,
    CompanyModule,
    EstablishmentModule,
    ProfileModule,
    CampaignModule,
    OwnPartnershipModule,
    MemberGetMemberModule,
    NgbModule,
    AppRoutingModule,
    ShareButtonsModule.forRoot(),
    LeafletModule.forRoot(),
    TranslateModule.forRoot({ loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] } }),
    NgxModalModule.forRoot(),
    /**
     * StoreModule.forRoot is imported once in the root module, accepting a reducer
     * function or object map of reducer functions. If passed an object of
     * reducers, combineReducers will be run creating your application
     * meta-reducer. This returns all providers for an @ngrx/store
     * based application.
     */
    StoreModule.forRoot(REDUCERS, { metaReducers }),

    /**
     * @ngrx/router-store keeps router state up-to-date in the store.
     */
    StoreRouterConnectingModule.forRoot(),

    /**
     * Store devtools instrument the store retaining past versions of state
     * and recalculating new states. This enables powerful time-travel
     * debugging.
     *
     * To use the debugger, install the Redux Devtools extension for either
     * Chrome or Firefox
     *
     * See: https://github.com/zalmoxisus/redux-devtools-extension
     */
    StoreDevtoolsModule.instrument({
      name: 'Allya Admin Store',
      logOnly: environment.PRODUCTION,
    }),

    /**
     * EffectsModule.forRoot() is imported once in the root module and
     * sets up the effects class to be initialized immediately when the
     * application starts.
     *
     * See: https://ngrx.io/guide/effects#registering-root-effects
     */
    EffectsModule.forRoot(EFFECTS),
    NgTicketsModule.forRoot({applicationName: 'Allya'})
  ],
  providers: [
    CookieService,
    AuthGuard,
    FremiumGuard,
    ToasterService,
    TrialToasterService,
    SearchService,
    UserService,
    StorageService,
    ReportsService,
    RequestsServices,
    EstablishmentsService,
    CashbackService,
    WalletService,
    OfferService,
    AccountsService,
    GTMService,
    VouchersService,
    InvitesService,
    EstablishmentRedirectionService,
    BsModalService,
  ]
})
export class AppModule { }
